import { Grid, Autocomplete, TextField, FormControl } from '@mui/material';
import styles from "../../../../Styles/ComponentStyles/ProfileStyle/SAndPComparison.module.scss";
import { useContext, useEffect, useState } from 'react';
import CanvasJSReact from '../../../../assets/canvasjs/canvasjs.react';
import { ProfileContext, ProfileContextPayload } from '../../../../Context/ProfileDetailsContext';
import { UserContext, UserContextPayload } from '../../../../Context/UserDetailContext';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const SAndPComparisonTab = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed, so March = 2

    // build-rb-app-15: the picker's max couples to the series' own last year (series_max_year from the API);
    // the calendar formula is only the fallback until the first comparison response lands.
    const clockMaxYear = currentMonth >= 2 ? currentYear - 1 : currentYear - 2;
    const [maxYear, setMaxYear] = useState<number>(clockMaxYear);

    const period = Array.from(
        { length: maxYear - 1930 + 1 },
        (_, index) => 1930 + index
    );

    const [startPeriod, setStartPeriod] = useState<any>(period[0]);
    const [endPeriod, setEndPeriod] = useState<any>(period[period.length - 1]);

    const [investorReturn, setInvestorReturn] = useState(3);
    const [spCap, setSPCap] = useState(14);

    const [fullResdata, setFullResdata] = useState({
        "endAmtSP": "",
        "endAmtSPFC": "",
        "endAmtIR": "",
        "avgAmtSP": "",
        "avgAmtSPFC": "",
        "avgAmtIR": "",
        "cmpAmtSP": "",
        "cmpAmtSPFC": "",
        "cmpAmtIR": ""
    });

    const {
        getGraphDetails,
    } = useContext(ProfileContext) as ProfileContextPayload;

    const { setFlagLoader } = useContext(UserContext) as UserContextPayload;

    const irList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    const spCapList = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20]

    const tempRows = [
        { id: 1, investment: 'S&P', endingAmount: fullResdata?.endAmtSP, averageReturn: fullResdata?.avgAmtSP, actualReturn: fullResdata?.cmpAmtSP, color: '#2B7FFF' },
        { id: 2, investment: `S&P - 0% Floor - ${spCap}% Cap`, endingAmount: fullResdata?.endAmtSPFC, averageReturn: fullResdata?.avgAmtSPFC, actualReturn: fullResdata?.cmpAmtSPFC, color: '#00C950' },
        { id: 3, investment: 'Investor Return', endingAmount: fullResdata?.endAmtIR, averageReturn: fullResdata?.avgAmtIR, actualReturn: fullResdata?.cmpAmtIR, color: '#FF6900' },
    ];

    const [graphDataNew, setGraphDataNew] = useState({
        animationEnabled: true,
        borderRadius: "10px !important",
        theme: "light2",
        title: {
            text: "S&P Comparison Chart",
            horizontalAlign: "left",
            fontSize: 20,
            fontColor: "#0F172B",
            fontWeight: "400",
            fontFamily: "Arial, sans-serif",
            padding: "20px"
        },
        legend: {
            horizontalAlign: "right",
            verticalAlign: "top",
            fontSize: 15,
            fontColor: "#0F172B",
            fontWeight: "400",
            fontFamily: "Arial, sans-serif",
        },
        axisX: {
            valueFormatString: "#0,.",
            titleFontColor: "#0F172B",
            labelFontColor: "#0F172B",
        },
        axisY: {
            prefix: "$",
            titleFontColor: "#0F172B",
            labelFontColor: "#0F172B",
            gridColor: "#E2E8F0", // Add this
            gridThickness: 1 // Optional: adjust grid line thickness
        },
        data: [{
            lineColor: "#2B7FFF",
            markerColor: "#2B7FFF",
            markerSize: 6, // Add this
            markerType: "circle", // Add this (optional)
            type: "spline",
            name: "S&P",
            showInLegend: true,
            toolTipContent: "<span style=\"color:#4F81BC\"><strong>{name}: </strong></span> ${y}<br><span style=\"color:#4F81BC\"><strong>Year: </strong></span> {label}",
            dataPoints: []
        }, {
            lineColor: "#00C950",
            markerColor: "#00C950",
            markerSize: 6, // Add this
            markerType: "circle", // Add this (optional)
            type: "spline",
            name: "S & P 0% Floor 14% Cap",
            showInLegend: true,
            toolTipContent: "<span style=\"color:#9bbb58\"><strong>{name}: </strong></span> ${y}<br><span style=\"color:#9bbb58\"><strong>Year: </strong></span> {label}",
            dataPoints: []
        }, {
            lineColor: "#FF6900",
            markerColor: "#FF6900",
            markerSize: 6, // Add this
            markerType: "circle", // Add this (optional)
            type: "spline",
            name: "Investor Return",
            showInLegend: true,
            toolTipContent: "<span style=\"color:#c0504e\"><strong>{name}: </strong></span> ${y}<br><span style=\"color:#c0504e\"><strong>Year: </strong></span> {label}",
            dataPoints: []
        }],
    });

    const handleStartPeriodChange = (_: any, newValue: string | null) => {
        if (newValue !== null) {
            setStartPeriod(newValue);
        }
    };

    const handleEndPeriodChange = (_: any, newValue: string | null) => {
        if (newValue !== null) {
            setEndPeriod(newValue);
        }
    };

    const handleInvestorReturnChange = (_: any, newValue: number | null) => {
        if (newValue !== null) {
            setInvestorReturn(newValue);
        }
    };

    const handleSPCapChange = (_: any, newValue: number | null) => {
        if (newValue !== null) {
            setSPCap(newValue);
        }
    };

    useEffect(() => {
        const fetchGraphDetails = async () => {
            try {
                setFlagLoader(true)
                let graphDetailsObj = {
                    "start_year": startPeriod,
                    "end_year": endPeriod,
                    "investor_return": investorReturn,
                    "sp_cap": spCap,
                    "start_amount": "100000"
                };
                const response = await getGraphDetails(graphDetailsObj);
                if (response?.status) {
                    if (response?.data) {
                        let finalData = JSON.parse(response?.data)
                        setFullResdata(finalData)
                        // Accept the clock only when it is a usable picker bound: a whole year from the picker's first year
                        // (1930) up to the current calendar year (a data year cannot lie in the future; an absurd integer would
                        // size the period array). Anything else leaves the last accepted clock (initially the calendar fallback).
                        // Both selected ends are pulled down to it so start <= end <= series_max_year on the next fetch
                        // (TRI 10-27-26 folds, rounds 1 and 2).
                        const apiMaxYear = finalData?.series_max_year
                        if (Number.isInteger(apiMaxYear) && apiMaxYear >= 1930 && apiMaxYear <= currentYear) {
                            setMaxYear(apiMaxYear)
                            setEndPeriod((prev: any) => (Number(prev) > apiMaxYear ? apiMaxYear : prev))
                            setStartPeriod((prev: any) => (Number(prev) > apiMaxYear ? apiMaxYear : prev))
                        }

                        let graph_data = JSON.parse(JSON.stringify(graphDataNew))
                        graph_data['data'][1]['name'] = (finalData?.mainspfcname)
                        graph_data['data'][0]['dataPoints'] = []
                        graph_data['data'][1]['dataPoints'] = []
                        graph_data['data'][2]['dataPoints'] = []
                        finalData.data.map((rr: { year: { toString: () => any; }; sp: any; spfc: any; ir: any; }) => {
                            graph_data['data'][0]['dataPoints'].push({ label: rr.year.toString(), y: rr.sp })
                            graph_data['data'][1]['dataPoints'].push({ label: rr.year.toString(), y: rr.spfc })
                            graph_data['data'][2]['dataPoints'].push({ label: rr.year.toString(), y: rr.ir })
                        })
                        setGraphDataNew(graph_data)
                    }
                    setFlagLoader(false)
                }
                else {
                    console.log("error fetching graph details")
                }
            } catch (error) {
                console.log(error, "Something Went Wrong!")
                setFlagLoader(false)
            } finally {
                setFlagLoader(false)
            }
        };

        fetchGraphDetails();
    }, [startPeriod, endPeriod, investorReturn, spCap]);

    return (
        <>
            <Grid container gap={"20px"} mb={'30px'}>
                <Grid item xs={12}>
                    <Grid
                        container
                        gap={"20px"}
                    >
                        <Grid item xs={5.9}>
                            <Grid
                                container
                                justifyContent={"space-between"}
                                className={styles.timePeriodContainer}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <Grid item>
                                    <Grid container gap={"10px"} alignItems={"center"}>
                                        <Grid item className={styles.iconContainer}>
                                            <CalendarTodayOutlinedIcon sx={{ fontSize: "20px" }} />
                                        </Grid>
                                        <Grid item className={styles.timePeriodText}>Time Period</Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.dateLabels}>Start Period</Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            value={startPeriod}
                                                            onChange={handleStartPeriodChange}
                                                            options={period}
                                                            getOptionLabel={(option) => option.toString()}
                                                            disableClearable
                                                            renderInput={(params) => (
                                                                <TextField {...params}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                                            border: "1px solid #E2E8F0"
                                                                        },
                                                                        "& .MuiFormControl-root, .MuiInputBase-root": {
                                                                            height: "100%"
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            padding: "0px 8px !important",
                                                                            backgroundColor: "rgba(255, 255, 255, 1)"
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.dateLabels}>End Period</Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            value={endPeriod}
                                                            onChange={handleEndPeriodChange}
                                                            options={period}
                                                            getOptionLabel={(option) => option.toString()}
                                                            renderInput={(params) => (
                                                                <TextField {...params}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                                            border: "1px solid #E2E8F0"
                                                                        },
                                                                        "& .MuiFormControl-root, .MuiInputBase-root": {
                                                                            height: "100%"
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            padding: "0px 8px !important",
                                                                            backgroundColor: "rgba(255, 255, 255, 1)"
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                            disableClearable
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={5.9}>
                            <Grid
                                container
                                justifyContent={"space-between"}
                                className={styles.timePeriodContainer}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <Grid item>
                                    <Grid container gap={"10px"} alignItems={"center"}>
                                        <Grid item className={styles.iconContainer}>
                                            <SettingsOutlinedIcon sx={{ fontSize: "22px" }} />
                                        </Grid>
                                        <Grid item className={styles.timePeriodText}>Variable</Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.dateLabels}>Investor Return</Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            value={investorReturn}
                                                            onChange={handleInvestorReturnChange}
                                                            options={irList}
                                                            getOptionLabel={(option) => `${option}%`}
                                                            renderInput={(params) => (
                                                                <TextField {...params}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                                            border: "1px solid #E2E8F0"
                                                                        },
                                                                        "& .MuiFormControl-root, .MuiInputBase-root": {
                                                                            height: "100%"
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            padding: "0px 8px !important",
                                                                            backgroundColor: "rgba(255, 255, 255, 1)",
                                                                        },
                                                                        cursor: "pointer"
                                                                    }}
                                                                />
                                                            )}
                                                            disableClearable
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Grid container gap={"5px"}>
                                                <Grid item xs={12} className={styles.dateLabels}>S&P Cap</Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            value={spCap}
                                                            onChange={handleSPCapChange}
                                                            options={spCapList}
                                                            getOptionLabel={(option) => `${option}%`}
                                                            renderInput={(params) => (
                                                                <TextField {...params}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                                            border: "1px solid #E2E8F0"
                                                                        },
                                                                        "& .MuiFormControl-root, .MuiInputBase-root": {
                                                                            height: "100%"
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            padding: "0px 8px !important",
                                                                            backgroundColor: "rgba(255, 255, 255, 1)"
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                            disableClearable
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid container className={styles.amountContainer}>
                        <Grid item xs={12}>
                            <Grid container className={styles.amountTextContainer}>
                                <Grid item className={styles.startAmtValue}>Comparision Analysis</Grid>
                                <Grid item >
                                    <span className={styles.startAmtTxt}>Start Amount: </span>
                                    <span className={styles.startAmtValue}>$100,000</span>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <table className={styles.table}>
                                <thead>
                                    <tr className={styles.tableRow}>
                                        <th className={`${styles.tableHeader}`} style={{ borderRadius: "5px 0px 0px 5px", border: "none" }}></th>
                                        <th className={`${styles.tableHeader} ${styles.tableHeaderRight}`} style={{ border: "none" }}>ENDING AMOUNT</th>
                                        <th className={`${styles.tableHeader} ${styles.tableHeaderRight}`} style={{ border: "none" }}>AVERAGE RATE OF RETURN</th>
                                        <th className={`${styles.tableHeader} ${styles.tableHeaderRight}`} style={{ borderRadius: "0px 5px 5px 0px", border: "none" }}>ACTUAL RATE OF RETURN</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tempRows.map((row: any) => (
                                        <tr key={row.id} className={styles.tableRow}>
                                            <td className={styles.tableCell}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ width: '12px', height: '12px', backgroundColor: row.color, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }}></span>
                                                    {row.investment}
                                                </span>
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.tableCellRight}`}>
                                                {row.endingAmount.toLocaleString()}
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.tableCellRight}`}>
                                                {row.averageReturn}
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.tableCellRight}`}>
                                                {row.actualReturn}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid
                    item
                    xs={12}
                >
                    <Grid container
                        sx={{
                            borderRadius: "5px",
                            border: "1px solid #E2E8F0"
                        }}>
                        {/* Title and Legend Section - Outside colored background */}
                        <Grid item xs={12} sx={{ padding: "20px 20px 10px 20px" }}>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <span style={{
                                        fontSize: "20px",
                                        fontWeight: 400,
                                        color: "#0F172B",
                                        fontFamily: "Arial, sans-serif"
                                    }}>
                                        S&P Comparison Chart
                                    </span>
                                </Grid>
                                <Grid item>
                                    <Grid container gap={2} alignItems="center">
                                        <Grid item style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ width: '12px', height: '12px', backgroundColor: '#2B7FFF', borderRadius: '50%', display: 'inline-block' }}></span>
                                            <span style={{ fontSize: '15px', color: '#0F172B', fontWeight: 500 }}>S&P</span>
                                        </Grid>
                                        <Grid item style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ width: '12px', height: '12px', backgroundColor: '#00C950', borderRadius: '50%', display: 'inline-block' }}></span>
                                            <span style={{ fontSize: '15px', color: '#0F172B', fontWeight: 500 }}>S&P 0% Floor {spCap}% Cap</span>
                                        </Grid>
                                        <Grid item style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ width: '12px', height: '12px', backgroundColor: '#FF6900', borderRadius: '50%', display: 'inline-block' }}></span>
                                            <span style={{ fontSize: '15px', color: '#0F172B', fontWeight: 500 }}>Investor Return</span>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Chart Section - With colored background */}
                        <Grid item xs={12} sx={{
                            backgroundColor: "#F2F6F9",
                            borderRadius: "5px",
                            margin: "10px 20px 20px 20px",
                            border: "1px solid #E2E8F0"
                        }}>
                            <div style={{ padding: "20px" }}>
                                <CanvasJSChart
                                    chartType="Line"
                                    options={{
                                        ...graphDataNew,
                                        title: {
                                            text: "" // Remove title from chart since it's now outside
                                        },
                                        legend: {
                                            ...graphDataNew.legend,
                                            horizontalAlign: "center", // Hide legend from chart
                                            verticalAlign: "bottom",
                                            fontSize: 0 // Hide the built-in legend
                                        },
                                        backgroundColor: "#F2F6F9" // Match the container background
                                    }}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

export default SAndPComparisonTab;